import { ThemeProvider } from 'react-jss';
import React, { useMemo, forwardRef, Suspense } from 'react';
import PropTypes from 'prop-types';

import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { CrudAudienceAdReact } from './crud-audience-ad-react';

const DelayedCopilotSearchMMAEditor =
  React.lazy(() => import(/* webpackMode: 'eager' */'./copilot-search-mma-editor')
    .then(({ CopilotSearchMMAEditor }) => ({
      default: CopilotSearchMMAEditor,
    })));

const DelayedBulkMMAConvertEditor =
  React.lazy(() => import(/* webpackMode: 'eager' */'./mma-creation-rsa/bulk-mma-convert-editor')
    .then(({ BulkMMAConvertEditor }) => ({
      default: BulkMMAConvertEditor,
    })));

const DelayedPlaceholderMMAEditor =
  React.lazy(() => import(/* webpackMode: 'eager' */'./placeholder-mma-editor')
    .then(({ PlaceholderMMAEditor }) => ({
      default: PlaceholderMMAEditor,
    })));

export const CrudAudienceAdReactWithContext = forwardRef((props, ref) => {
  const scopeValue = useMemo(() => ({
    scope: {
      values: {
        cid: props.cid,
        aid: props.aid,
        uid: props.uid,
      },
    },
  }), [props.cid, props.aid, props.uid]);

  let CoreEditor = CrudAudienceAdReact;

  if (props.permissions.dynamic.MMACopilot && props.isMultimediaAd) {
    if (props.isPlaceholderMMA) {
      CoreEditor = DelayedPlaceholderMMAEditor;
    } else if (!props.isRecommendation && !props.isBulkEditMMA) {
      CoreEditor = DelayedCopilotSearchMMAEditor;
    }
  }

  if (props.permissions.dynamic.MMARefactor && props.isMultimediaAd && props.isBulkEditMMA) {
    CoreEditor = DelayedBulkMMAConvertEditor;
  }

  return (
    <Suspense fallback={<div />}>
      <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
        <ServerContext.Provider value={props.serverContext}>
          <I18nContext.Provider value={props.i18n}>
            <InstrumentationContext.Provider value={props.instrumentation}>
              <PreferenceContext.Provider value={props.preferencesService}>
                <ScopeContext.Provider value={scopeValue}>
                  <PermissionsContext.Provider value={props.permissions}>
                    <Scenario name="mma-ad-editor">
                      <Activity name="mma-ad-editor">
                        <CoreEditor
                          {...props}
                          ref={ref}
                          editorRef={ref}
                        />
                      </Activity>
                    </Scenario>
                  </PermissionsContext.Provider>
                </ScopeContext.Provider>
              </PreferenceContext.Provider>
            </InstrumentationContext.Provider>
          </I18nContext.Provider>
        </ServerContext.Provider>
      </ThemeProvider>
    </Suspense>
  );
});

CrudAudienceAdReactWithContext.propTypes = {
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  uid: PropTypes.string,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  preferencesService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }),
  serverContext: PropTypes.shape({
    ServicesEndpoints: PropTypes.shape({
      CampaignAPI: PropTypes.string,
    }),
    Token: PropTypes.string,
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.string,
    }),
    CurrentAccount: PropTypes.shape({
      Id: PropTypes.string,
    }),
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      MMACopilot: PropTypes.bool,
      MMARefactor: PropTypes.bool,
    }),
  }).isRequired,
  isMultimediaAd: PropTypes.bool,
  isPlaceholderMMA: PropTypes.bool,
  isRecommendation: PropTypes.bool,
  isBulkEditMMA: PropTypes.bool,
};

CrudAudienceAdReactWithContext.defaultProps = {
  preferencesService: {},
  isMultimediaAd: false,
  isPlaceholderMMA: false,
  isRecommendation: false,
  isBulkEditMMA: false,
  uid: '0',
};
